import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'


// IST Date helpers
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  policyCrmDbName?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  FlexiName: string | null
  FlexiBasePremium: number | null
  FlexiGst: number | null
  FlexiTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  BaseSumInsured: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

type TopupPlan = {
  name: string
  sumInsured: number
  roomRentIncrease: number
  premium: number
}

type FlexiOption = {
  name: string
  label: string
  description: string
  premium: number
}

const RELATIONS = ['Spouse', 'Child', 'Parent', 'Parent-in-Law']
const GENDERS = ['Male', 'Female', 'Other']
const GST_RATE = 0.18

// Grade-based sum insured mapping for TaskUs
// Grade1 = 1,50,000 | Grade2 = 3,50,000 | Grade3 = 5,00,000
const GRADE_SUM_INSURED: Record<string, number> = {
  'Grade1': 150000,
  'Grade2': 350000,
  'Grade3': 500000,
  'grade1': 150000,
  'grade2': 350000,
  'grade3': 500000,
  'Grade 1': 150000,
  'Grade 2': 350000,
  'Grade 3': 500000,
  '1': 150000,
  '2': 350000,
  '3': 500000,
}

const getGradeSumInsured = (grade: string | undefined): number => {
  if (!grade) return 150000
  if (GRADE_SUM_INSURED[grade]) return GRADE_SUM_INSURED[grade]
  const match = grade.match(/(\d+)/)
  if (match) {
    const num = match[1]
    if (GRADE_SUM_INSURED[num]) return GRADE_SUM_INSURED[num]
  }
  return 150000 // default to Grade1
}

// Topup Plans - E+S+4C+2P/2PL (Per Family, Excl. GST)
const TOPUP_PLANS: TopupPlan[] = [
  { name: 'Topup200000', sumInsured: 200000, roomRentIncrease: 2000, premium: 10348 },
  { name: 'Topup300000', sumInsured: 300000, roomRentIncrease: 2000, premium: 12211 },
  { name: 'Topup500000', sumInsured: 500000, roomRentIncrease: 2000, premium: 14286 },
  { name: 'Topup700000', sumInsured: 700000, roomRentIncrease: 2000, premium: 16428 },
  { name: 'Topup900000', sumInsured: 900000, roomRentIncrease: 2000, premium: 18070 },
  { name: 'Topup1000000', sumInsured: 1000000, roomRentIncrease: 2000, premium: 19879 },
]

// Flexi Options
const FLEXI_OPTIONS: FlexiOption[] = [
  {
    name: 'EnhancedMaternity',
    label: 'Enhanced Maternity Limit',
    description: 'Normal: INR 25,000 + C-section: INR 25,000 (additional to base policy coverage)',
    premium: 7080,
  },
  {
    name: 'CopayNonMedicalWaiver',
    label: 'Co-Pay and Non Medical Charges Waiver',
    description: '100% Waive off Co-Pay + Non Medical Charges of base policy',
    premium: 7700,
  },
  {
    name: 'BothFlexiCombined',
    label: 'Enhanced Maternity + Co-Pay Waiver (Combined)',
    description: 'Enhanced Maternity Limit + 100% Co-Pay Waiver + Non Medical Charges Waiver',
    premium: 14780,
  },
]

const getAgeAsOfDate = (dob: string, asOfDate: string) => {
  const birthDate = new Date(dob)
  const refDate = new Date(asOfDate)
  if (Number.isNaN(birthDate.getTime()) || Number.isNaN(refDate.getTime())) return 0

  let age = refDate.getFullYear() - birthDate.getFullYear()
  const birthdayThisYear = new Date(refDate.getFullYear(), birthDate.getMonth(), birthDate.getDate())

  if (refDate < birthdayThisYear) {
    age--
  } else if (refDate > birthdayThisYear) {
    age++
  }

  return age
}

export const TaskusBaseEnrollmentForm: React.FC<Props> = ({
  policyData,
  policyEffectiveDate,
  policyExpiryDate,
  policyCrmDbName,
  onSuccess,
  preloadedMembers,
  initialDependents,
}) => {
  const gstRate = GST_RATE

  // ─── Pro-rata helpers ───────────────────────────────────────────────
  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const getProrationFactor = (): number => {
    let dojRaw: any = undefined
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) { dojRaw = row.JoiningDate; break }
        if (row?.joiningdate != null) { dojRaw = row.joiningdate; break }
        if (row?.joiningDate != null) { dojRaw = row.joiningDate; break }
      }
    }
    if (dojRaw == null) dojRaw = policyData.DOJ

    const dojMs = parseDateOnlyToUtcMs(dojRaw)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()
  const isProrated = prorationFactor < 1
  // ─────────────────────────────────────────────────────────────────────────

  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedTopup, setSelectedTopup] = useState<TopupPlan | null>(null)
  const [hasSelectedTopup, setHasSelectedTopup] = useState(false)
  const [selectedFlexi, setSelectedFlexi] = useState<FlexiOption | null>(null)
  const [hasSelectedFlexi, setHasSelectedFlexi] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: '',
  })

  const baseSumInsured = getGradeSumInsured(policyData.Grade)

  // Employee + Spouse + 4 Children + 2 Parents/Parent-in-Laws = max 7 dependents (excluding self)
  const maxTotalDependents = 7

  const getLatestDate = () => {
    const policyDate = policyEffectiveDate ? new Date(policyEffectiveDate) : null
    const dojDate = policyData.DOJ ? new Date(policyData.DOJ) : null

    if (policyDate && dojDate) {
      return policyDate > dojDate ? policyEffectiveDate! : policyData.DOJ
    }
    return policyDate ? policyEffectiveDate! : dojDate ? policyData.DOJ : new Date().toISOString()
  }

  const ageReferenceDate = getLatestDate()

  useEffect(() => {
    if (preloadedMembers && preloadedMembers.length > 0) {
      setEnrolledMembers(preloadedMembers)
      setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
      const mappedDependents = initialDependents
        .map((item, index) => {
          let relation = item.Relation
          const rLower = relation ? relation.toLowerCase() : ''

          if (['husband', 'wife', 'spouse'].includes(rLower)) relation = 'Spouse'
          else if (['son', 'daughter', 'child'].includes(rLower)) relation = 'Child'
          else if (['father', 'mother', 'parent'].includes(rLower)) relation = 'Parent'
          else if (['father-in-law', 'mother-in-law', 'parent-in-law'].includes(rLower)) relation = 'Parent-in-Law'

          return {
            id: item.sid || Date.now() + index,
            name: item.Name,
            dob: item.DOB ? toISTInputFormat(item.DOB) : '',
            relation: relation,
            gender: item.Gender || '',
          }
        })
        .filter(
          (dep) => dep.relation === 'Self' || dep.relation === 'Spouse' || dep.relation === 'Child' || dep.relation === 'Parent' || dep.relation === 'Parent-in-Law'
        )
      setDependents(mappedDependents)
    } else {
      setDependents([
        {
          id: 1,
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '',
        },
      ])
    }
  }, [policyData, preloadedMembers, initialDependents])

  const calculatePremium = () => {
    // Apply pro-rata to topup premium
    const rawTopupPremium = selectedTopup ? selectedTopup.premium : 0
    const topupPremium = Math.round(rawTopupPremium * prorationFactor)
    const topupGST = Math.round(topupPremium * gstRate)
    const topupTotal = topupPremium + topupGST

    // Apply pro-rata to flexi premium
    const rawFlexiPremium = selectedFlexi ? selectedFlexi.premium : 0
    const flexiPremium = Math.round(rawFlexiPremium * prorationFactor)
    const flexiGST = Math.round(flexiPremium * gstRate)
    const flexiTotal = flexiPremium + flexiGST

    const totalGST = topupGST + flexiGST
    const totalPayable = topupTotal + flexiTotal

    return {
      dependentPremium: 0,
      dependentCount: 0,
      dependentGST: 0,
      dependentTotal: 0,
      topupPremium,
      topupGST,
      topupTotal,
      flexiPremium,
      flexiGST,
      flexiTotal,
      gst: totalGST,
      total: totalPayable,
    }
  }

  const handleAddClick = () => {
    setError(null)
    const familyDependents = dependents.filter((d) => d.relation !== 'Self')

    if (familyDependents.length >= maxTotalDependents) {
      setError(`Maximum of ${maxTotalDependents} dependents allowed (1 Spouse + 4 Children + 2 Parents/Parent-in-Laws).`)
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: '',
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (!RELATIONS.includes(dep.relation)) {
      setError('Only Spouse, Child, Parent and Parent-in-Law relations are allowed.')
      return false
    }

    const age = getAgeAsOfDate(dep.dob, ageReferenceDate)

    if (dep.relation === 'Spouse' && (age < 18 || age > 99)) {
      setError('Spouse must be between 18 and 99 years of age.')
      return false
    }

    if (dep.relation === 'Child' && age > 28) {
      setError('Child must be 28 years or below.')
      return false
    }

    if ((dep.relation === 'Parent' || dep.relation === 'Parent-in-Law') && (age < 18 || age > 99)) {
      setError('Parent/Parent-in-Law must be between 18 and 99 years of age.')
      return false
    }

    // Exclude the currently-editing dependent from counts
    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    const relationCounts = otherDeps.reduce((acc, curr) => {
      acc[curr.relation] = (acc[curr.relation] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    if (dep.relation === 'Spouse' && relationCounts['Spouse']) {
      setError('Only 1 Spouse allowed.')
      return false
    }

    if (dep.relation === 'Child' && (relationCounts['Child'] || 0) >= 4) {
      setError('Maximum 4 Children allowed.')
      return false
    }

    // Parents + Parent-in-Laws combined max 2
    const totalParents = (relationCounts['Parent'] || 0) + (relationCounts['Parent-in-Law'] || 0)
    if ((dep.relation === 'Parent' || dep.relation === 'Parent-in-Law') && totalParents >= 2) {
      setError('Maximum 2 Parents/Parent-in-Laws allowed (combined).')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    if (editingDependentId) {
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    setDependents(dependents.filter((d) => d.id !== id))
    setError(null)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()

    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: 0,
        Grade: policyData.Grade,
        crm_client_id: policyCrmDbName || policyData.CorpCode,
        Email: currentUser?.Email,
        CorporateCode: policyData.CorporateCode,
        SumInsured: baseSumInsured,
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: Boolean(selectedTopup) || Boolean(selectedFlexi),
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,

          TopupName: selectedTopup?.name || '',
          topupsumInsured: selectedTopup?.sumInsured || 0,
          topup_base_premium: premiumData.topupPremium,
          topup_gst: premiumData.topupGST,
          topup_total_premium: premiumData.topupTotal,

          FlexiName: selectedFlexi?.name || '',
          flexi_base_premium: premiumData.flexiPremium,
          flexi_gst: premiumData.flexiGST,
          flexi_total_premium: premiumData.flexiTotal,

          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      return {
        ...baseMember,
        premium: 0,
        base_premium: 0,
        base_gst: 0,
        base_total_premium: 0,
      }
    })

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch(
        'https://etl.futurisk.in/webhook/0abdc08f-17ce-4a9d-acc2-67442061e808',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(familyMembersWithPremium),
        }
      )

      if (response.ok) {
        setShowSuccess(true)
      } else {
        setError('Failed to submit enrollment. Please try again.')
        setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()

  // ─── Frozen / Read-only State ───────────────────────────────────────
  if (policyData.freezeEnrolment) {
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0))
      return (
        <div className='text-center p-5 text-primary'>
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span> Loading
          enrollment details...
        </div>
      )

    const membersToDisplay =
      preloadedMembers && preloadedMembers.length > 0 ? preloadedMembers : enrolledMembers

    const frozenPremium = {
      topupPremium: membersToDisplay.reduce((acc, m) => acc + (m.TopupBasePremium || 0), 0),
      flexiPremium: membersToDisplay.reduce((acc, m) => acc + (m.FlexiBasePremium || 0), 0),
      gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
      total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    const optedTopupMember = membersToDisplay.find((m) => m.TopupName)
    const optedTopup = optedTopupMember?.TopupName
    const optedTopupSI = optedTopupMember?.TopupSumInsured

    const optedFlexiMember = membersToDisplay.find((m) => m.FlexiName)
    const optedFlexi = optedFlexiMember?.FlexiName

    return (
      <div className='card'>
        <div className='card-body'>
          <Row>
            <Col md={8}>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0'>Enrolled Family Details</h3>
                {baseSumInsured > 0 && (
                  <div className='badge badge-light-primary fs-6 p-3'>
                    Base Sum Insured: ₹{baseSumInsured.toLocaleString('en-IN')} ({policyData.Grade})
                  </div>
                )}
              </div>
              <div className='text-muted fs-7 mb-3'>
                Coverage: Employee + Spouse + 4 Dependent Children (upto 28 yrs) + 2 Parents or 2 Parent-in-Laws
              </div>
              <div className='table-responsive mb-5'>
                <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Date of Birth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToDisplay.map((member) => (
                      <tr key={member.Id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <div className='text-dark fw-bolder fs-6'>{member.Name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{member.Relation}</td>
                        <td>{member.Gender}</td>
                        <td>{formatDateIST(member.Dob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Selected Top-up Plan */}
              <h3 className='mb-3'>Selected Top-up Plan</h3>
              <div className='alert alert-info d-flex align-items-center mb-3'>
                <i className='bi bi-info-circle-fill text-info fs-3 me-3'></i>
                <div className='text-gray-700 fw-bold fs-7'>
                  Top-Up coverage provides additional sum insured over and above base family coverage (E+S+4C+2P/2PL).
                </div>
              </div>
              <div className='p-4 bg-light rounded border border-gray-300 mb-5'>
                {optedTopup ? (
                  <div className='fw-bold fs-5 text-gray-800'>
                    {optedTopup}{' '}
                    {optedTopupSI
                      ? `— Sum Insured: ₹${optedTopupSI.toLocaleString('en-IN')}`
                      : ''}
                  </div>
                ) : (
                  <div className='text-muted'>No Top-up plan selected.</div>
                )}
              </div>

              {/* Selected Flexi Option */}
              <h3 className='mb-3'>Selected Flexi Option</h3>
              <div className='p-4 bg-light rounded border border-gray-300'>
                {optedFlexi ? (
                  <div className='fw-bold fs-5 text-gray-800'>
                    {FLEXI_OPTIONS.find(f => f.name === optedFlexi)?.label || optedFlexi}
                  </div>
                ) : (
                  <div className='text-muted'>No Flexi option selected.</div>
                )}
              </div>
            </Col>

            <Col md={4}>
              <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                <h4 className='mb-4'>Premium Summary</h4>

                {frozenPremium.topupPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Top-up Premium:</strong>
                    <div className='float-end'>₹{frozenPremium.topupPremium.toLocaleString('en-IN')}</div>
                  </div>
                )}

                {frozenPremium.flexiPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Flexi Premium:</strong>
                    <div className='float-end'>₹{frozenPremium.flexiPremium.toLocaleString('en-IN')}</div>
                  </div>
                )}

                {frozenPremium.total > 0 ? (
                  <>
                    <hr />
                    <div className='mb-2'>
                      <span>Taxable Amount:</span>
                      <span className='float-end'>
                        ₹{(frozenPremium.topupPremium + frozenPremium.flexiPremium).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className='mb-2'>
                      <span>GST (18%):</span>
                      <span className='float-end'>₹{frozenPremium.gst.toLocaleString('en-IN')}</span>
                    </div>
                    <hr />
                    <h5 className='text-primary'>
                      Total Payable:
                      <span className='float-end'>₹{frozenPremium.total.toLocaleString('en-IN')}</span>
                    </h5>
                    <div className='text-muted fs-7 mt-2 text-end'>
                      The total premium will be deducted from your next salary.
                    </div>
                  </>
                ) : (
                  <div className='text-muted text-center py-5'>
                    No additional premium applicable.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // ─── Success State ──────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <i className='bi bi-check-circle-fill text-success fs-3x'></i>
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>
          {premiumInfo.total > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of <strong>₹{premiumInfo.total.toLocaleString('en-IN')}</strong> will be
                deducted from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ─── Active Enrollment State ────────────────────────────────────────
  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
          <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>{error}</div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h3 className='mb-0'>Family Details</h3>
                {baseSumInsured > 0 && (
                  <div className='text-primary fw-bold mt-1'>
                    Base Sum Insured: ₹{baseSumInsured.toLocaleString('en-IN')} ({policyData.Grade})
                  </div>
                )}
                <div className='text-muted fs-7 mt-1'>
                  Coverage: Employee + Spouse + 4 Dependent Children (upto 28 yrs) + 2 Parents or 2 Parent-in-Laws
                </div>
              </div>
              <Button
                variant='primary'
                onClick={handleAddClick}
                disabled={dependents.filter((d) => d.relation !== 'Self').length >= maxTotalDependents}
              >
                Add Dependent
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => (
                    <tr key={dep.id} className='text-capitalize text-dark'>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                              {dep.name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.relation}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.gender}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {formatDateIST(dep.dob)}
                        </span>
                      </td>
                      <td>
                        {dep.relation !== 'Self' && (
                          <div className='d-flex gap-2'>
                            <Button
                              variant='primary'
                              size='sm'
                              onClick={() => handleEditClick(dep)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              onClick={() => removeDependent(dep.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ─── Top-up Plans Section ─────────────────────────── */}
            <div className='mb-5'>
              <h3 className='mb-3'>Optional Top-up Plans (E+S+4C+2P/2PL)</h3>

              <div className='alert alert-warning d-flex align-items-center mb-4'>
                <i className='bi bi-info-circle-fill text-warning fs-3 me-3'></i>
                <div className='text-gray-700 fw-bold'>
                  Top-Up coverage provides additional sum insured for you and your family.
                  Room Rent Limit is increased by ₹2,000 with each top-up option.
                  If you choose this option, the total premium (excl. GST) will be deducted from your next salary.
                </div>
              </div>

              {/* Radio Selection */}
              <div className='border rounded p-4 bg-light-primary'>
                <div className='fw-bold fs-6 mb-3 text-gray-800'>Select Your Top-up Plan:</div>
                <Form.Group>
                  <Form.Check
                    type='radio'
                    label={<span className='fw-bold text-gray-700'>No Top-up</span>}
                    name='topupPlan'
                    id='topup-none'
                    checked={hasSelectedTopup && selectedTopup === null}
                    onChange={() => { setSelectedTopup(null); setHasSelectedTopup(true) }}
                    className='mb-3 p-2 ps-6 rounded border bg-white'
                  />
                  {TOPUP_PLANS.map((plan, idx) => (
                    <Form.Check
                      key={idx}
                      type='radio'
                      label={
                        <span className='fw-bold'>
                          <span
                            className={`badge ${selectedTopup?.name === plan.name ? 'badge-success' : 'badge-light-primary'} me-2`}
                          >
                            Option {idx + 1}
                          </span>
                          Additional Sum Insured: ₹{plan.sumInsured.toLocaleString('en-IN')} |
                          Room Rent +₹{plan.roomRentIncrease.toLocaleString('en-IN')} &mdash; Premium: ₹
                          {plan.premium.toLocaleString('en-IN')}
                          <span className='text-muted fs-8'> + GST</span>
                        </span>
                      }
                      name='topupPlan'
                      id={`topup-${idx}`}
                      checked={hasSelectedTopup && selectedTopup?.name === plan.name}
                      onChange={() => { setSelectedTopup(plan); setHasSelectedTopup(true) }}
                      className={`mb-2 p-2 ps-6 rounded border ${
                        selectedTopup?.name === plan.name
                          ? 'bg-light-success border-success'
                          : 'bg-white'
                      }`}
                    />
                  ))}
                </Form.Group>
              </div>
            </div>

            {/* ─── Flexi Options Section ─────────────────────────── */}
            <div className='mb-5'>
              <h3 className='mb-3'>Flexi Benefits</h3>

              <div className='alert alert-info d-flex align-items-center mb-4'>
                <i className='bi bi-info-circle-fill text-info fs-3 me-3'></i>
                <div className='text-gray-700 fw-bold'>
                  Choose one of the flexi benefit options below, or select the combined option for both benefits.
                  The premium will be deducted from your salary.
                </div>
              </div>

              <div className='border rounded p-4 bg-light-info'>
                <div className='fw-bold fs-6 mb-3 text-gray-800'>Select Your Flexi Option:</div>
                <Form.Group>
                  <Form.Check
                    type='radio'
                    label={<span className='fw-bold text-gray-700'>No Flexi Benefit</span>}
                    name='flexiPlan'
                    id='flexi-none'
                    checked={hasSelectedFlexi && selectedFlexi === null}
                    onChange={() => { setSelectedFlexi(null); setHasSelectedFlexi(true) }}
                    className='mb-3 p-2 ps-6 rounded border bg-white'
                  />
                  {FLEXI_OPTIONS.map((option, idx) => (
                    <Form.Check
                      key={idx}
                      type='radio'
                      label={
                        <div>
                          <span className='fw-bold'>
                            <span
                              className={`badge ${selectedFlexi?.name === option.name ? 'badge-success' : 'badge-light-info'} me-2`}
                            >
                              Option {idx + 1}
                            </span>
                            {option.label} &mdash; Premium: ₹{option.premium.toLocaleString('en-IN')}
                            <span className='text-muted fs-8'> + GST</span>
                          </span>
                          <div className='text-muted fs-8 ms-8 mt-1'>{option.description}</div>
                        </div>
                      }
                      name='flexiPlan'
                      id={`flexi-${idx}`}
                      checked={hasSelectedFlexi && selectedFlexi?.name === option.name}
                      onChange={() => { setSelectedFlexi(option); setHasSelectedFlexi(true) }}
                      className={`mb-2 p-2 ps-6 rounded border ${
                        selectedFlexi?.name === option.name
                          ? 'bg-light-success border-success'
                          : 'bg-white'
                      }`}
                    />
                  ))}
                </Form.Group>
              </div>
            </div>
          </Col>

          {/* ─── Premium Summary Sidebar ─────────────────────────── */}
          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>

              <div className='mb-3 p-3 bg-white rounded border'>
                <div className='text-muted fs-7'>Base Sum Insured ({policyData.Grade})</div>
                <div className='fw-bolder fs-4 text-primary'>
                  ₹{baseSumInsured.toLocaleString('en-IN')}
                </div>
                <div className='text-muted fs-8 mt-1'>No premium for base coverage</div>
              </div>

              {selectedTopup && (
                <div className='mb-3'>
                  <strong>Top-up Premium:</strong>
                  <div className='float-end'>₹{premiumInfo.topupPremium.toLocaleString('en-IN')}</div>
                  <div className='text-muted fs-8'>
                    GST: ₹{premiumInfo.topupGST.toLocaleString('en-IN')} |
                    Total: ₹{premiumInfo.topupTotal.toLocaleString('en-IN')}
                  </div>
                </div>
              )}

              {selectedFlexi && (
                <div className='mb-3'>
                  <strong>Flexi Premium ({selectedFlexi.label}):</strong>
                  <div className='float-end'>₹{premiumInfo.flexiPremium.toLocaleString('en-IN')}</div>
                  <div className='text-muted fs-8'>
                    GST: ₹{premiumInfo.flexiGST.toLocaleString('en-IN')} |
                    Total: ₹{premiumInfo.flexiTotal.toLocaleString('en-IN')}
                  </div>
                </div>
              )}

              {isProrated && (selectedTopup || selectedFlexi) && (
                <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                  <span className='badge badge-warning me-2'>Pro-rated</span>
                  <span className='text-dark fs-7'>
                    Premium calculated for{' '}
                    <strong>{Math.round(prorationFactor * 365)} days</strong> remaining in the
                    policy year (factor: {(prorationFactor * 100).toFixed(1)}%).
                  </span>
                </div>
              )}

              {(selectedTopup || selectedFlexi) ? (
                <>
                  <hr />
                  <div className='mb-2'>
                    <span>Taxable Amount:</span>
                    <span className='float-end'>
                      ₹{(premiumInfo.topupPremium + premiumInfo.flexiPremium).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className='mb-2'>
                    <span>GST (18%):</span>
                    <span className='float-end'>₹{premiumInfo.gst.toLocaleString('en-IN')}</span>
                  </div>
                  <hr />
                  <h5 className='text-primary'>
                    Total Payable:
                    <span className='float-end'>₹{premiumInfo.total.toLocaleString('en-IN')}</span>
                  </h5>
                  <div className='text-muted fs-7 mt-2 text-end'>
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-3'>
                  No additional premium applicable.
                  <br />
                  <small>Select a Top-up or Flexi plan to see premium.</small>
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button
                  variant='success'
                  size='lg'
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting || !hasSelectedTopup || !hasSelectedFlexi}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
                {(!hasSelectedTopup || !hasSelectedFlexi) && (
                  <div className='text-muted text-center fs-7 mt-1'>
                    Please select both top-up and flexi options to proceed
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add / Edit Dependent Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setEditingDependentId(null)
          setError(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingDependentId ? 'Edit Dependent' : 'Add Dependent'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
              {error}
            </div>
          )}
          <div className='alert alert-light-info border border-info p-3 mb-4'>
            <div className='fs-8 text-gray-700'>
              <strong>Age Limits:</strong> Children: 0–28 years
            </div>
          </div>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={newDependent.name}
                onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select
                value={newDependent.relation}
                onChange={(e) => setNewDependent({ ...newDependent, relation: e.target.value })}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={newDependent.gender}
                onChange={(e) => setNewDependent({ ...newDependent, gender: e.target.value })}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type='date'
                value={newDependent.dob}
                onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowAddModal(false)
              setEditingDependentId(null)
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' onClick={saveDependent}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning d-flex align-items-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-warning me-4'>
              <i className='bi bi-exclamation-triangle-fill fs-2'></i>
            </span>
            <div className='d-flex flex-column'>
              {premiumInfo.total > 0 && (
                <>
                  <div className='fw-bold mb-1'>
                    Total Premium Deduction: ₹{premiumInfo.total.toLocaleString('en-IN')}
                  </div>
                  {premiumInfo.topupTotal > 0 && (
                    <div className='small'>Top-up: ₹{premiumInfo.topupTotal.toLocaleString('en-IN')} (incl. GST)</div>
                  )}
                  {premiumInfo.flexiTotal > 0 && (
                    <div className='small'>Flexi: ₹{premiumInfo.flexiTotal.toLocaleString('en-IN')} (incl. GST)</div>
                  )}
                  <div className='small'>This amount will be debited from your salary.</div>
                </>
              )}
              <div className='small mt-1'>
                <strong>
                  Please review and confirm your nominations carefully. Once submitted, no changes
                  can be made in the policy year.
                </strong>
              </div>
            </div>
          </div>
          <p className='mb-0 mt-3'>Do you want to proceed to enroll your family?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Family'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
